import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';

const GlobalHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Don't show header on auth pages to avoid interference
  if (location.pathname === '/auth' || location.pathname === '/admin-register') {
    return null;
  }

  return (
    <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="/logo.png" 
              alt="Food Connect Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          {/* Navigation Menu */}
          <nav className="hidden lg:flex items-center space-x-6">
            <button 
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-green-600 transition-colors font-medium"
              data-testid="nav-home"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/how-it-works')}
              className="text-gray-600 hover:text-green-600 transition-colors font-medium"
              data-testid="nav-how-it-works"
            >
              How it Works
            </button>
            <button 
              onClick={() => navigate('/about')}
              className="text-gray-600 hover:text-green-600 transition-colors font-medium"
              data-testid="nav-about-us"
            >
              About Us
            </button>
            <button 
              onClick={() => navigate('/partner-restaurants')}
              className="text-gray-600 hover:text-green-600 transition-colors font-medium"
              data-testid="nav-partner-restaurants"
            >
              Partner Restaurants
            </button>
            <button 
              onClick={() => navigate('/partner-ngos')}
              className="text-gray-600 hover:text-green-600 transition-colors font-medium"
              data-testid="nav-partner-ngos"
            >
              Partner NGOs
            </button>
            {/* Transport Network temporarily hidden - uncomment to re-enable */}
            {/* <button 
              onClick={() => navigate('/transport-network')}
              className="text-gray-600 hover:text-green-600 transition-colors font-medium"
              data-testid="nav-transport-network"
            >
              Transport Network
            </button> */}
            <button 
              onClick={() => navigate('/volunteer-network')}
              className="text-gray-600 hover:text-green-600 transition-colors font-medium"
              data-testid="nav-volunteer-network"
            >
              Volunteer Network
            </button>
          </nav>
          
          {/* Mobile Menu Button & Get Started */}
          <div className="flex items-center space-x-4">
            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 text-gray-600 hover:text-green-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            
            {/* Action Buttons based on current page */}
            {location.pathname === '/dashboard' ? (
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
                data-testid="back-home-btn"
              >
                Back to Home
              </Button>
            ) : (
              <>
                {location.pathname !== '/' && (
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/')}
                    data-testid="back-home-btn"
                  >
                    Back to Home
                  </Button>
                )}
                <Button 
                  onClick={() => navigate('/auth')} 
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="header-login-btn"
                >
                  {location.pathname === '/' ? 'Get Started' : 'Join Us'}
                </Button>
              </>
            )}
          </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t">
            <nav className="flex flex-col space-y-4 pt-4">
              <button 
                onClick={() => {
                  navigate('/');
                  setMobileMenuOpen(false);
                }}
                className="text-gray-600 hover:text-green-600 transition-colors font-medium text-left"
                data-testid="mobile-nav-home"
              >
                Home
              </button>
              <button 
                onClick={() => {
                  navigate('/how-it-works');
                  setMobileMenuOpen(false);
                }}
                className="text-gray-600 hover:text-green-600 transition-colors font-medium text-left"
                data-testid="mobile-nav-how-it-works"
              >
                How it Works
              </button>
              <button 
                onClick={() => {
                  navigate('/about');
                  setMobileMenuOpen(false);
                }}
                className="text-gray-600 hover:text-green-600 transition-colors font-medium text-left"
                data-testid="mobile-nav-about-us"
              >
                About Us
              </button>
              <button 
                onClick={() => {
                  navigate('/partner-restaurants');
                  setMobileMenuOpen(false);
                }}
                className="text-gray-600 hover:text-green-600 transition-colors font-medium text-left"
                data-testid="mobile-nav-partner-restaurants"
              >
                Partner Restaurants
              </button>
              <button 
                onClick={() => {
                  navigate('/partner-ngos');
                  setMobileMenuOpen(false);
                }}
                className="text-gray-600 hover:text-green-600 transition-colors font-medium text-left"
                data-testid="mobile-nav-partner-ngos"
              >
                Partner NGOs
              </button>
              {/* Transport Network temporarily hidden - uncomment to re-enable */}
              {/* <button 
                onClick={() => {
                  navigate('/transport-network');
                  setMobileMenuOpen(false);
                }}
                className="text-gray-600 hover:text-green-600 transition-colors font-medium text-left"
                data-testid="mobile-nav-transport-network"
              >
                Transport Network
              </button> */}
              <button 
                onClick={() => {
                  navigate('/volunteer-network');
                  setMobileMenuOpen(false);
                }}
                className="text-gray-600 hover:text-green-600 transition-colors font-medium text-left"
                data-testid="mobile-nav-volunteer-network"
              >
                Volunteer Network
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default GlobalHeader;